/**
 * Projects → Evidence sub-agent persisted render payload (AGE-455 / AGE-493).
 *
 * The live Evidence timeline is streamed as transient ``evidence.chunk``
 * frames (see ``evidence-sessions.ts``) — gone after a reload. AGE-455
 * makes the output durable: on completion the BE persists a normalized
 * render payload on the harness tool result, which reaches the FE at
 * ``tool_result.provider_metadata.data.evidence`` via the run snapshot
 * (``GET /agents/runs/{run_id}``) and lands on the merged ``ToolUseItem``
 * as ``result_provider_metadata`` — no extra fetch.
 *
 * Reconciliation contract (AGE-455): the persisted payload is
 * authoritative once the parent tool call has completed; the live stream
 * is in-flight data only. ``EvidenceSessionInlineBlock`` prefers the
 * session built here over the live atom for the same tool_call_id.
 *
 * The payload's ``evidence_chunks`` are persisted in the standalone
 * Evidence chat's ``additional_metadata.evidence_chunks`` shape verbatim
 * (index-ordered; web_search / library_search / text / medical_calculator
 * / patient_summary). ``buildEvidenceSessionFromPayload`` mirrors the
 * standalone reload conversion in ``convertChatMessages.ts`` for exactly
 * that renderable subset, producing the same ``TimelineEntry`` shapes the
 * live reducer emits — so ``EvidenceTimelineBody`` renders both sources
 * identically. Source parsing is shared (``parseSources``), not forked,
 * because it carries citation-branding rules (EVI-590 / EVI-1105) that
 * must not drift.
 */

import type {
  CalculatorItemEntry,
  FormulaCalculatorItemEntry,
  LibrarySearchItemEntry,
  PatientSummaryItemEntry,
  SessionConversation,
  TextItemEntry,
  TimelineEntry,
  WebSearchItemEntry,
} from '@/components/chat/types/types';
import { ChatMode } from '@/components/chat/types/types';
import { parseSources } from '@/components/chat/utils/convertChatMessages';
import { logger } from '@/utils/logger/logger';
import type {
  CalculatorResult,
  EvidenceImage,
  FormulaCalculationStep,
  FormulaCalculatorCategory,
  FormulaCalculatorResult,
  FormulaCriteriaBreakdown,
  SimpleCitation,
} from '@heidi/evidence/contracts';
import { z } from 'zod';

// ── Wire schema ─────────────────────────────────────────────────────

/**
 * Envelope fields are validated; chunk entries stay opaque records —
 * the chunk shape is owned by the Evidence surface and evolves
 * independently (same trust-boundary stance as the live path's
 * ``inner`` handling in ``evidence-sessions.ts``).
 *
 * The BE serializes with ``exclude_none``: optional fields are simply
 * absent. ``version`` / ``truncated`` / ``partial`` are always present,
 * but parse defensively anyway — a malformed payload must degrade to
 * "no payload", never crash the transcript.
 */
const evidenceRenderPayloadSchema = z.object({
  // Versioned persisted contract: only render shapes we know. A future
  // breaking bump fails the parse and degrades to the no-payload
  // fallback instead of mis-rendering as v1.
  version: z.literal(1),
  evidence_chunks: z.array(z.record(z.unknown())).default([]),
  sources: z.array(z.record(z.unknown())).optional(),
  structured_citations: z.array(z.record(z.unknown())).optional(),
  annotations: z.array(z.record(z.unknown())).optional(),
  web_search_query: z.string().optional(),
  truncated: z.boolean().default(false),
  truncation_reason: z.string().optional(),
  partial: z.boolean().default(false),
  error: z.string().optional(),
});

export type EvidenceRenderPayload = z.infer<typeof evidenceRenderPayloadSchema>;

/**
 * Structural view of one persisted chunk — the renderable subset of the
 * standalone ``additional_metadata.evidence_chunks`` element shape.
 */
interface PersistedEvidenceChunk {
  type?: string;
  index?: number;
  // text
  text?: string;
  citations?: SimpleCitation[];
  // web / library search
  query?: string;
  sources?: Array<Record<string, unknown>>;
  images?: EvidenceImage[];
  // medical calculator
  calculator?: string;
  result?: CalculatorResult;
  // formula calculator (calculator_result, EVI-620)
  formula_name?: string;
  formula_expression?: string;
  formula_display?: string;
  category?: FormulaCalculatorCategory;
  variables?: Record<string, number | string | boolean>;
  source_url?: string;
  source_name?: string;
  validation_reference?: string;
  criteria_breakdown?: FormulaCriteriaBreakdown[];
  calculation_steps?: FormulaCalculationStep[];
  warnings?: string[];
  // patient summary
  patient_profile_id?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

// ── Reader ──────────────────────────────────────────────────────────

/**
 * Pull the persisted render payload off a merged ``ToolUseItem``'s
 * ``result_provider_metadata``. Detection is by presence of
 * ``data.evidence`` — NOT by tool name (``provider_metadata.tool_name``
 * carries the exposed alias, and ``data`` has unrelated siblings like
 * ``subagent_run_id`` / ``tool_interactions``).
 *
 * Returns ``undefined`` for pre-AGE-455 results (no payload persisted),
 * malformed payloads, and payloads with no renderable chunks — all of
 * which keep the caller on today's fallback behaviour.
 */
export const readEvidenceRenderPayload = (
  metadata: Record<string, unknown> | undefined
): EvidenceRenderPayload | undefined => {
  const data = isRecord(metadata) ? metadata.data : undefined;
  const evidence = isRecord(data) ? data.evidence : undefined;
  if (evidence === undefined) return undefined;

  const parsed = evidenceRenderPayloadSchema.safeParse(evidence);
  if (!parsed.success) {
    logger.debug('[projects/evidence-render-payload] Dropping malformed payload', {
      issues: parsed.error.issues,
    });
    return undefined;
  }
  // An empty chunk list cannot render a timeline — treat as no payload so
  // the inline block keeps its honest "completed with no output" hint.
  if (parsed.data.evidence_chunks.length === 0) return undefined;
  return parsed.data;
};

// ── Builder ─────────────────────────────────────────────────────────

/**
 * Convert a persisted payload into the same ``SessionConversation``
 * shape the live reducer produces, scoped to one tool call. Terminal by
 * construction: ``streamingState`` is null and every entry is
 * ``completed``.
 *
 * Mirrors ``convertChatMessages.ts``'s evidence_chunks loop: index-sort
 * (unindexed chunks keep array position), per-type entry mapping for
 * the full persisted union (text / web_search / library_search /
 * medical_calculator / calculator_result / patient_summary), citation
 * ``source_type`` normalization (EVI-590), and the same skip rules for
 * malformed calculator / patient-summary chunks. Entry ids are
 * deterministic per ``toolCallId`` so reloads produce stable React keys.
 *
 * Returns ``undefined`` when no chunk survives the skip rules — an
 * empty timeline would render ``EvidenceTimelineBody``'s "starting"
 * loader on a terminal call, so the caller must fall back to the
 * no-payload behaviour instead.
 */
export const buildEvidenceSessionFromPayload = (
  payload: EvidenceRenderPayload,
  toolCallId: string
): SessionConversation | undefined => {
  // Trust-boundary cast: the schema validated the envelope; chunk
  // internals are owned by the Evidence surface (see module docblock).
  const chunks = payload.evidence_chunks as PersistedEvidenceChunk[];
  const sortedChunks = [...chunks].sort(
    (a, b) => (a.index ?? Infinity) - (b.index ?? Infinity)
  );

  const timeline: TimelineEntry[] = [];
  let position = 0;

  // The schema validates the envelope only — chunk internals are
  // untrusted BE output. Guard every array access (``Array.isArray``
  // below) so a malformed field degrades that chunk rather than throwing,
  // and wrap the whole loop so anything unforeseen still falls back to
  // "no payload" instead of crashing the transcript render (this runs in
  // a ``useMemo`` with no error boundary above it).
  try {
    sortedChunks.forEach((chunk, idx) => {
      const chunkIndex = chunk.index ?? idx;

      if (chunk.type === 'web_search') {
        const entry: WebSearchItemEntry = {
          type: 'web_search_item',
          id: `web-search-${toolCallId}-${chunkIndex}`,
          query: chunk.query,
          sources: parseSources(
            Array.isArray(chunk.sources) ? chunk.sources : undefined,
            toolCallId,
            chunkIndex
          ),
          ...(Array.isArray(chunk.images) && chunk.images.length
            ? { images: chunk.images }
            : {}),
          status: 'completed',
          position: position++,
          backendIndex: chunkIndex,
          mode: ChatMode.EVIDENCE,
        };
        timeline.push(entry);
      } else if (chunk.type === 'library_search') {
        const entry: LibrarySearchItemEntry = {
          type: 'library_search_item',
          id: `library-search-${toolCallId}-${chunkIndex}`,
          query: chunk.query,
          sources: parseSources(
            Array.isArray(chunk.sources) ? chunk.sources : undefined,
            toolCallId,
            chunkIndex
          ),
          status: 'completed',
          position: position++,
          backendIndex: chunkIndex,
          mode: ChatMode.EVIDENCE,
        };
        timeline.push(entry);
      } else if (chunk.type === 'medical_calculator') {
        if (chunk.calculator && chunk.result) {
          const entry: CalculatorItemEntry = {
            type: 'calculator_item',
            id: `calc-${toolCallId}-${chunkIndex}`,
            toolId: `${toolCallId}-calc-${chunkIndex}`,
            calculator: chunk.calculator,
            status: 'completed',
            result: chunk.result,
            position: position++,
            backendIndex: chunkIndex,
            mode: ChatMode.EVIDENCE,
          };
          timeline.push(entry);
        }
      } else if (chunk.type === 'calculator_result') {
        // EVI-620 formula calculator — either the name or a result is
        // enough to render, matching the standalone converter.
        if (chunk.formula_name || chunk.result) {
          const resultData = chunk.result as FormulaCalculatorResult | undefined;
          const formulaResult: FormulaCalculatorResult | undefined = resultData
            ? {
                value: resultData.value,
                unit: resultData.unit,
                interpretation: resultData.interpretation,
                risk_level: resultData.risk_level,
              }
            : undefined;
          const entry: FormulaCalculatorItemEntry = {
            type: 'formula_calculator_item',
            id: `formula-calc-${toolCallId}-${chunkIndex}`,
            toolId: `${toolCallId}-formula-calc-${chunkIndex}`,
            formulaName: chunk.formula_name || 'Calculation',
            formulaExpression: chunk.formula_expression,
            formulaDisplay: chunk.formula_display,
            category: chunk.category || 'general_math',
            status: 'completed',
            variables: chunk.variables,
            result: formulaResult,
            sourceUrl: chunk.source_url,
            sourceName: chunk.source_name,
            validationReference: chunk.validation_reference,
            criteriaBreakdown: chunk.criteria_breakdown,
            calculationSteps: chunk.calculation_steps,
            warnings: chunk.warnings,
            position: position++,
            backendIndex: chunkIndex,
            mode: ChatMode.EVIDENCE,
          };
          timeline.push(entry);
        }
      } else if (chunk.type === 'patient_summary') {
        if (chunk.patient_profile_id) {
          const entry: PatientSummaryItemEntry = {
            type: 'patient_summary_item',
            id: `patient-summary-${toolCallId}-${chunkIndex}`,
            toolId: `${toolCallId}-patient-summary-${chunkIndex}`,
            status: 'completed',
            patientProfileId: chunk.patient_profile_id,
            position: position++,
            backendIndex: chunkIndex,
            mode: ChatMode.EVIDENCE,
          };
          timeline.push(entry);
        }
      } else {
        // ``text`` and any unknown future chunk type — same fallthrough the
        // standalone converter uses (unknown degrades to text). Textless
        // chunks render nothing, so skip them — otherwise an all-empty
        // payload would defeat the no-renderable-entries guard below.
        if (!chunk.text) return;
        const rawCitations = Array.isArray(chunk.citations) ? chunk.citations : [];
        const citations: SimpleCitation[] = rawCitations.map((c) => ({
          ...c,
          // EVI-590: backend sends ``type``, frontend checks ``source_type``.
          source_type:
            c.source_type ||
            ((c as { type?: string }).type as SimpleCitation['source_type']),
        }));
        const entry: TextItemEntry = {
          type: 'text_item',
          id: `${toolCallId}-chunk-${chunkIndex}`,
          content: chunk.text,
          annotations: [],
          citations,
          // One group per sub-agent call: the BE persists the synthesized
          // answer as N text chunks; ``useTextGrouping`` folds them back
          // into a single TextResponse exactly like the live path.
          groupId: toolCallId,
          position: position++,
          backendIndex: chunkIndex,
          mode: ChatMode.EVIDENCE,
        };
        timeline.push(entry);
      }
    });
  } catch (error) {
    // A malformed chunk slipped past the per-field guards — honour the
    // "degrade to no payload, never crash" contract rather than letting
    // it bubble through the render.
    logger.debug('[projects/evidence-render-payload] Build threw; dropping payload', {
      error: error instanceof Error ? error.message : String(error),
    });
    return undefined;
  }

  // Every chunk was skipped (malformed calculators, textless chunks, …)
  // — nothing renderable, so report "no payload" to the caller.
  if (timeline.length === 0) return undefined;

  return {
    timeline,
    streamingState: null,
    metadata: { lastActivity: Date.now() },
  };
};
